import React, { useCallback, useEffect, useRef, useState } from 'react';
import classes from "./Game.module.css";
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { finishQuizSession, startQuizSession, submitQuizSessionAnswer } from '../http/http';
import { resetQuestions } from '../store/slices/questionsSlice';
import { DecreaseTime, IncreaseTimeForProgress, ResetTime, SetProgress } from '../store/slices/timeSlice';
import CircularProgress from '@mui/material/CircularProgress';
import { IncreaseIndex, ResetIndex } from '../store/slices/indexSlice';
import { increaseCorrectAnswer, increaseWrongAnswer, resetUserScore, userScoreUpdate } from '../store/slices/userPlayerSlice';
import { opponentScoreUpdate, resetOpponentScore } from '../store/slices/opponentPlayerSlice';
import CorrectAudio from '../components/CorrectAudio';
import WrongAudio from '../components/WrongAudio';
import ClockAudio from '../components/ClockAudio';
import MediumLoader from '../components/Loaders/MediumLoader';
import { FaArrowLeft } from "react-icons/fa6";
import Lottie from 'lottie-react';
import GameOverAnimation from "../animations/gameOver.json";
import { useTranslation } from 'react-i18next';
import { removeUserToken } from '../store/slices/userTokenSlice';
import { toast } from 'react-toastify';

const Game = () => {
    const location = useLocation();
    const { userToken } = useSelector(state => state.persisted.userTokenSlice);
    const { timeClock, progress } = useSelector((state) => state?.persisted?.timeSlice);
    const { index } = useSelector(state => state?.persisted?.indexSlice);
    const { user, userScore } = useSelector(state => state?.persisted?.userPlayerSlice);
    const { opponent, opponentScore } = useSelector(state => state?.persisted?.opponentPlayerSlice);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);

    const [gameOverLoading, setGameOverLoading] = useState(false);

    const [correct, setCorrect] = useState(null);
    const [wrong, setWrong] = useState(null);

    const [wrongSound, setWrongSound] = useState(null);
    const [correctSound, setCorrectSound] = useState(null);
    const [pointerEvents, setPointerEvents] = useState("all");

    const { selectedLanguage } = useSelector(state => state.languageSlice);

    const navigate = useNavigate();
    const { t } = useTranslation();
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [sessionId, setSessionId] = useState("");
    const [questionCount, setQuestionCount] = useState(0);
    const answerPendingRef = useRef(false);
    const finishPendingRef = useRef(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setCurrentQuestion(null);
        setSessionId("");
        setQuestionCount(0);
        setGameOverLoading(false);
        answerPendingRef.current = false;
        finishPendingRef.current = false;
        dispatch(ResetIndex());
        dispatch(ResetTime());
        dispatch(resetQuestions());
        dispatch(resetUserScore());
        dispatch(resetOpponentScore());
        startQuizSession(
            "standard",
            location?.state?.qid?.id,
            location?.state?.level?.id,
            selectedLanguage,
            userToken
        ).then(response => {
            if (!active) {
                return;
            }
            const result = response.data.result;
            setSessionId(result.sessionId);
            setCurrentQuestion(result.question);
            setQuestionCount(result.questionCount);
            setLoading(false);
        }).catch(error => {
            if (!active) {
                return;
            }
            if (error?.response?.status === 403) {
                dispatch(removeUserToken());
                toast.error(t("52"));
                navigate("/");
            } else {
                toast.error(error?.response?.data?.error || "Unable to start this quiz.");
            }
            setLoading(false);
        });
        return () => {
            active = false;
        };
    }, [dispatch, location.key, location.state, navigate, selectedLanguage, t, userToken]);

    useEffect(() => {
        if (!loading) {
            const intervalId = setInterval(() => {
                dispatch(DecreaseTime());
                dispatch(IncreaseTimeForProgress());
                dispatch(SetProgress());
            }, 1000);

            return () => clearInterval(intervalId);
        }
    }, [timeClock, dispatch, loading]);

    const finishSession = useCallback(async (finalResult = null) => {
        if (finishPendingRef.current || !sessionId) {
            return;
        }
        finishPendingRef.current = true;
        setGameOverLoading(true);
        try {
            const response = finalResult
                ? { data: { result: finalResult } }
                : await finishQuizSession(sessionId, userToken);
            const result = response.data.result;
            if (!finalResult) {
                const scoreDifference = Number(result.score || 0) - userScore;
                if (scoreDifference) {
                    dispatch(userScoreUpdate(scoreDifference));
                }
            }
            navigate("/game-over", {
                state: { ...location.state, sessionResult: result }
            });
        } catch (error) {
            finishPendingRef.current = false;
            setGameOverLoading(false);
            if (error?.response?.status === 403) {
                dispatch(removeUserToken());
                toast.error(t("52"));
                navigate("/");
            } else {
                toast.error(error?.response?.data?.error || "Unable to finish this quiz.");
            }
        }
    }, [dispatch, location.state, navigate, sessionId, t, userScore, userToken]);

    useEffect(() => {
        if (timeClock < 0 && !loading && !gameOverLoading && !answerPendingRef.current) {
            finishSession();
        }
    }, [timeClock, loading, gameOverLoading, finishSession]);

    const checkAnswerAndNextQuestion = async (selectedOption, selectedOptionName) => {
        if (!sessionId || answerPendingRef.current || finishPendingRef.current || !currentQuestion) {
            return;
        }
        answerPendingRef.current = true;
        setPointerEvents("none");
        try {
            const response = await submitQuizSessionAnswer(sessionId, index, selectedOption, userToken);
            const result = response.data.result;
            if (result.expired) {
                finishSession(result);
                return;
            }
            const reviewQuestion = {
                title: currentQuestion.title,
                selectedOptionText: selectedOptionName
            };
            if (result.pointsEarned) {
                dispatch(userScoreUpdate(result.pointsEarned));
            }
            dispatch(opponentScoreUpdate(location?.state?.level?.perQuestion || 0));
            if (result.isCorrect) {
                setCorrect(String(selectedOption));
                setWrong(null);
                setCorrectSound(true);
                setWrongSound(false);
                dispatch(increaseCorrectAnswer(reviewQuestion));
            } else {
                setWrong(String(selectedOption));
                setCorrect(null);
                setWrongSound(true);
                setCorrectSound(false);
                dispatch(increaseWrongAnswer({
                    question: reviewQuestion,
                    yourAnswer: selectedOptionName
                }));
            }
            setTimeout(() => {
                setCorrect(null);
                setWrong(null);
                setCorrectSound(false);
                setWrongSound(false);
                if (result.finished) {
                    finishSession(result);
                    return;
                }
                dispatch(IncreaseIndex());
                setCurrentQuestion(result.nextQuestion);
                setPointerEvents("all");
                answerPendingRef.current = false;
            }, 650);
        } catch (error) {
            answerPendingRef.current = false;
            setPointerEvents("all");
            if (error?.response?.status === 403) {
                dispatch(removeUserToken());
                toast.error(t("52"));
                navigate("/");
            } else {
                toast.error(error?.response?.data?.error || "Unable to save this answer.");
            }
        }
    };


    return (
        <>
            <ClockAudio />
            {correctSound && <CorrectAudio />}
            {wrongSound && <WrongAudio />}
            {/* <Header hideLanguage={true} /> */}
            <div className={classes.header}>

                <div className={classes.back_button} onClick={() => {
                    if (sessionId) {
                        finishSession();
                    } else {
                        navigate(-1);
                    }
                }}>
                    <FaArrowLeft className={classes.back_icon} />
                </div>
            </div>

            <div className={classes.container}>
                <div className={classes.section_header}>
                    <div className={classes.section_user_image}>
                        <div className={classes.user_image_container}>
                            <img src={user?.image ?? "/assets/images/dummy.png"} alt={t("1")} className={classes.user_image} />
                        </div>
                        <div className={classes.user_score_container}>
                            <p className={classes.user_name}>{t("1")}</p>
                            <p className={classes.score}>{userScore}</p>
                        </div>
                    </div>

                    <div className={classes.clock_container}>
                        <div className={classes.circular_clock_container}>
                            <CircularProgress variant="determinate" color="primary" value={progress} />
                            <p className={classes.clock_time} style={{color:timeClock<=10 && "red"}}>
                                {timeClock}
                            </p>
                        </div>
                    </div>

                    <div className={classes.section_opponent_image}>
                        <div className={classes.user_score_container}>
                            <p className={classes.user_name}>{opponent?.name ?? t("17")}</p>
                            <p className={classes.score}>{opponentScore}</p>
                        </div>
                        <div className={classes.user_image_container}>
                            <img src={opponent?.image ?? "/assets/images/dummy.png"} alt="opponent" className={classes.user_image} />
                        </div>

                    </div>
                </div>
                {loading ?
                    <div className={classes.game_over_animation_container}>
                        <MediumLoader />
                    </div>
                    : gameOverLoading ?
                        <div className={classes.game_over_animation_container}>
                            <Lottie animationData={GameOverAnimation} loop autoPlay className={classes.gameOverAnimation} />
                        </div>
                        :
                        currentQuestion && <div className={classes.question_container}
                            style={{ pointerEvents: pointerEvents }}
                        >
                            <p className={classes.question}>{index + 1}/{questionCount}. {currentQuestion.title}</p>
                            {currentQuestion.options.map(option => (
                                <div
                                    key={option.id}
                                    className={`${classes.option_container} ${
                                        correct === String(option.id)
                                            ? classes.right
                                            : wrong === String(option.id)
                                                ? classes.wrong
                                                : ""
                                    }`}
                                    onClick={() => checkAnswerAndNextQuestion(option.id, option.text)}
                                >
                                    <p className={classes.option}>{option.id}). {option.text}</p>
                                </div>
                            ))}
                        </div>
                }



            </div>

        </>

    )
}

export default Game